import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, X, Check, ClipboardList } from 'lucide-react';

// Define default functions outside component to maintain stable references
const defaultGetItemId = (item) => item._id;
const defaultGetItemKey = (item) => item._id;
const defaultGetItemTitle = (item) => item.title;

const MultiSelectSearchFilter = ({
  items = [],
  selectedItems = [],
  onSelectionChange,
  title,
  placeholder = "Search...",
  itemIcon: ItemIcon = ClipboardList,
  getItemId = defaultGetItemId,
  getItemTitle = defaultGetItemTitle,
  getItemKey = defaultGetItemKey,
  emptyMessage = "No items found"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const safeItems = Array.isArray(items) ? items : [];
  const [filteredItems, setFilteredItems] = useState(safeItems);
  const dropdownRef = useRef(null);

  // Filter items based on search term
  useEffect(() => {
    const list = Array.isArray(items) ? items : [];
    const filtered = list.filter(item =>
      getItemTitle(item).toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredItems(filtered);
  }, [items, searchTerm, getItemTitle]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleItemToggle = (item) => {
    const itemId = getItemId(item);
    const isSelected = selectedItems.includes(itemId);
    
    if (isSelected) {
      onSelectionChange(selectedItems.filter(id => id !== itemId));
    } else {
      onSelectionChange([...selectedItems, itemId]);
    }
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  const handleIndividualRemove = (event, item) => {
    event.stopPropagation(); // Prevent dropdown toggle
    const itemId = getItemId(item);
    onSelectionChange(selectedItems.filter(id => id !== itemId));
  };

  const renderSelectedBadges = () => {
    if (selectedItems.length === 0) {
      return <span className="text-sm text-gray-500">Select {title.toLowerCase()}...</span>;
    }

    const selectedObjects = selectedItems.map(id => 
      items.find(item => getItemId(item) === id)
    ).filter(Boolean);

    // Responsive badge limit: 2 on mobile, 3 on desktop
    const maxVisible = window.innerWidth < 768 ? 2 : 3;
    const visibleBadges = selectedObjects.slice(0, maxVisible);
    const remainingCount = selectedObjects.length - maxVisible;

    return (
      <div className="flex flex-wrap gap-1 items-center min-h-[24px]">
        {visibleBadges.map(item => (
          <span 
            key={getItemId(item)} 
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4] max-w-28 sm:max-w-32"
          >
            <span className="truncate">{getItemTitle(item)}</span>
            <button 
              onClick={(e) => handleIndividualRemove(e, item)} 
              className="ml-1 hover:bg-[#1086a4] hover:text-white rounded-full p-0.5 transition-colors flex-shrink-0"
              title={`Remove ${getItemTitle(item)}`}
              aria-label={`Remove ${getItemTitle(item)}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {remainingCount > 0 && (
          <span className="text-xs text-gray-500 px-1 whitespace-nowrap">
            +{remainingCount} more
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <ItemIcon className="w-4 h-4 text-[#1086a4]" />
          <span className="text-sm font-medium text-[#054581]">Filter by {title}</span>
        </div>
        <button
          onClick={handleClearAll}
          disabled={selectedItems.length === 0}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
            selectedItems.length > 0
              ? 'bg-red-500 hover:bg-red-600 text-white shadow-sm hover:shadow-md transform hover:scale-105 cursor-pointer'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed opacity-75'
          }`}
        >
          <X className="w-3 h-3" />
          {selectedItems.length > 0 ? `Clear all (${selectedItems.length})` : 'Clear all'}
        </button>
      </div>
      
      <div className="relative" ref={dropdownRef}>
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-lg cursor-pointer hover:border-[#1086a4] transition-colors min-h-[48px]"
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <ItemIcon className="w-4 h-4 text-[#1086a4] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              {renderSelectedBadges()}
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`} />
        </div>

        {isOpen && (
          <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={placeholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="max-h-48 overflow-y-auto">
              {filteredItems.length === 0 ? (
                <div className="p-3 text-sm text-gray-500 text-center">
                  {searchTerm ? `No ${title.toLowerCase()} found matching "${searchTerm}"` : emptyMessage}
                </div>
              ) : (
                filteredItems.map(item => {
                  const itemId = getItemId(item);
                  const itemKey = getItemKey(item);
                  const isSelected = selectedItems.includes(itemId);
                  
                  return (
                    <div
                      key={itemKey}
                      onClick={() => handleItemToggle(item)}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                        isSelected ? 'bg-[#1086a4] border-[#1086a4]' : 'border-gray-300'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-sm text-gray-700 flex-1">{getItemTitle(item)}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectSearchFilter;